import { BancoClient } from "bancojs";
import { useAuthStore } from "@/stores/auth";

/**
 * El cliente de la API, como singleton de módulo.
 *
 * A propósito NO es un store de Pinia: no es estado reactivo y envolverlo en uno
 * —como hacía ApiService— no aportaba nada. Los sitios de llamada quedan
 * `banco.admin.users.find(...)` en vez de `$api.client.admin.users.find(...)`.
 */
let instance: BancoClient | null = null;

/**
 * Construye el cliente. Se llama una vez durante el arranque, DESPUÉS de
 * inicializar el store de autenticación: los interceptores lo consultan.
 */
export function createApiClient(baseUrl: string): BancoClient {
  const client = new BancoClient({ baseUrl });
  const auth = useAuthStore();

  client.use(
    // ---- petición: inyecta un token fresco ------------------------------
    async (req) => {
      // Los endpoints permitAll de la API (/public/key, /subsidiary/**) vienen
      // marcados con auth:false desde el SDK. No les mandamos el token.
      if (!req.auth) return req;
      const token = await auth.ensureFreshToken();
      if (token) req.headers.set("Authorization", `Bearer ${token}`);
      return req;
    },

    // ---- respuesta: renueva y reintenta UNA vez ante un 401 -------------
    async (res, req, retry) => {
      if (res.status !== 401) return res;

      // Un solo reintento por petición. Sin esta marca, un 401 persistente
      // —usuario inactivo, o sin fila en banco_users— giraría en bucle.
      const marker = req.headers.get("X-Retry-401");
      if (marker) return res;

      const token = await auth.ensureFreshToken();
      if (!token) return res;

      req.headers.set("X-Retry-401", "1");
      req.headers.set("Authorization", `Bearer ${token}`);
      return retry();
    }
  );

  instance = client;
  return client;
}

/**
 * Proxy de acceso al cliente. Es un Proxy y no la instancia directa para que los
 * módulos puedan hacer `import { banco }` en el nivel superior sin depender del
 * orden de arranque: la resolución ocurre en la primera propiedad que se lee.
 */
export const banco = new Proxy({} as BancoClient, {
  get(_target, prop) {
    if (!instance) {
      throw new Error(
        "Se usó el cliente de la API antes de createApiClient(). Revisa el orden en main.ts."
      );
    }
    return Reflect.get(instance, prop);
  },
});
